import { getSubjects } from "@/lib/utils/dataFetching";
import { SchoolMembershipData } from "@tsTypes/admin-tools-schema";
import {
  UserCoreRegistrationFields,
  UserRegisterSchema,
  UserSchema,
} from "@tsTypes/next-auth";
import {
  CouponMeta,
  CouponType,
  CustomStripePrice,
  StripeProductMetadata,
} from "@tsTypes/product-schema";
import Stripe from "stripe";
import { SelectedSubscriptionMeta } from "../(core)/register/types";
import { generateVerificationToken } from "../../lib/strapi/verification";
import { getStripe } from "./stripe/../../../lib/stripe/builders";

const stripe = getStripe();

/**
 * Registers a new user and returns the user schema.
 * @param {UserCoreRegistrationFields} userMeta - The user registration metadata
 * @returns {Promise<UserSchema>} The registered user schema
 */
export async function registerUser(userMeta: UserCoreRegistrationFields) {
  // sending of email verification hooked into back-end automatically on account creation
  const { emailVerificationToken, emailVerificationExpiration } =
    generateVerificationToken();
  const newMeta: UserRegisterSchema = {
    ...userMeta,
    emailVerificationToken,
    emailVerificationExpiration,
    verified: false,
  };
  const res = await fetch(
    `${process.env.NEXT_PUBLIC_STRAPI_BASE_URL}/auth-local/register`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${process.env.STRAPI_API_TOKEN}`,
      },
      body: JSON.stringify(newMeta),
    }
  );
  const response = await res.json();
  if (response.error) {
    throw new Error(response.error.message);
  }
  return response.user as UserSchema;
}

/**
 * Registers a new coordinator user and returns the user schema.
 * @param {UserCoreRegistrationFields & { RelatedSchool: UserSchema["school"] }} userMeta - The coordinator registration metadata
 * @returns {Promise<UserSchema>} The registered coordinator user schema
 */
export async function registerCoordinatorUser(
  userMeta: UserCoreRegistrationFields & {
    RelatedSchool: UserSchema["schoolDocumentId"];
  }
) {
  const newMeta: UserCoreRegistrationFields & {
    verified: true;
  } = {
    ...userMeta,
    verified: true,
  };
  const res = await fetch(
    `${process.env.NEXT_PUBLIC_STRAPI_BASE_URL}/auth-local/register`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${process.env.STRAPI_API_TOKEN}`,
      },
      body: JSON.stringify(newMeta),
    }
  );
  const response = await res.json();
  if (response.error) {
    throw new Error(response.error.message);
  }
  return response.user as UserSchema;
}

/**
 * Creates a new Stripe customer and returns the customer ID.
 * @param {string} userDocumentId - The user ID
 * @param {string} userName - The user's name
 * @param {string} userEmail - The user's email
 * @returns {Promise<string>} The Stripe customer ID
 */
export async function createStripeCustomer(
  userDocumentId: string,
  userName: string,
  userEmail: string
) {
  const { id: stripeId } = await stripe.customers.create({
    name: userName,
    email: userEmail,
    metadata: {
      strapiId: userDocumentId,
    },
  });
  if (!stripeId) {
    throw new Error(
      "We couldn't contact Stripe to create a customer profile. Please try again!"
    );
  }
  return stripeId;
}

/**
 * Updates user metadata in the database.
 * @param {string} JWT - The user's JWT
 * @param {string} userDocumentId - The user ID
 * @param {object} newValues - The new values to update
 * @returns {Promise<void>} Resolves when update is complete
 */
export async function updateUserMeta(
  JWT: string,
  userDocumentId: string,
  newValues: {}
) {
  const res = await fetch(
    `${process.env.NEXT_PUBLIC_STRAPI_BASE_URL}/auth-local/update-user`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${JWT}`,
      },
      body: JSON.stringify({ userDocumentId, data: newValues }),
    }
  );
  if (!res.ok) {
    throw new Error(
      "Error syncing your information to our database. Please try again later!"
    );
  }
  return;
}

/**
 * Updates school metadata in the database.
 * @param {number} schoolId - The school ID
 * @param {object} newValues - The new values to update
 * @returns {Promise<void>} Resolves when update is complete
 */
export async function updateSchoolAccessTokenMeta(
  schoolDocumentId: string,
  newValues: {}
) {
  const res = await fetch(
    `${process.env.NEXT_PUBLIC_STRAPI_BASE_URL}/schools/${schoolDocumentId}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${process.env.STRAPI_API_TOKEN}`,
      },
      body: JSON.stringify({ data: { ...newValues } }),
    }
  );
  const response = await res.json();
  if (response.error) {
    throw new Error(
      "Error syncing your information to our database. Please try again later!"
    );
  }
  return;
}

/**
 * Retrieves a user by email address.
 * @param {string} userEmail - The user's email address
 * @returns {Promise<UserSchema>} The user schema
 */
export async function retrieveUserIdFromEmail(userEmail: string) {
  const res = await fetch(
    `${process.env.NEXT_PUBLIC_STRAPI_BASE_URL}/users/?filters[username][$eq]=${userEmail}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${process.env.STRAPI_API_TOKEN}`,
      },
    }
  );
  const response = await res.json();
  if (response.error) {
    throw new Error("No user could be found for that email.");
  }
  return response[0] as UserSchema;
}

/**
 * Retrieves institutional Stripe products.
 * @returns {Promise<Stripe.ApiSearchResult>} The Stripe products
 */
async function getInstitutionalStripeProducts() {
  const test = await stripe.prices.search({ query: "name" });
  return test;
}

/**
 * Retrieves a Stripe price object for a given price and membership.
 * @param {Stripe} stripe - The Stripe instance
 * @param {string} price - The price ID
 * @param {string} membership - The membership product ID
 * @returns {Promise<Stripe.Price>} The Stripe price object
 */
export async function retrieveStripePrice(
  stripe: Stripe,
  price: string,
  membership: string
) {
  const stripePrice = await stripe.prices.retrieve(price, {
    expand: ["currency_options"],
  });
  if (
    !stripePrice ||
    !stripePrice.unit_amount ||
    stripePrice.product !== membership
  ) {
    throw new Error(
      "Something has gone wrong contacting stripe. Please get in touch with us."
    );
  }
  return stripePrice;
}

export function getSubscriptionItemPriceId(
  item: Stripe.SubscriptionItem
): string {
  return typeof item.price === "string" ? item.price : item.price.id;
}

/**
 * Builds the items array for Stripe subscription updates and invoice previews.
 * Shared logic: for each selectedPriceId, keep existing item or add new; mark
 * existing items not in the selection as deleted. Matches updateSubscriptionItems
 * and invoices.createPreview(subscription_details.items).
 *
 * @param existingItems - subscription.items.data (expand items.data.price when unit_amount needed elsewhere)
 * @param selectedPriceIds - target set of price IDs after the update
 * @returns items for subscriptions.update or subscription_details.items
 */
export function buildSubscriptionUpdateItems(
  existingItems: Stripe.SubscriptionItem[],
  selectedPriceIds: string[]
): Stripe.SubscriptionUpdateParams.Item[] {
  const out: Stripe.SubscriptionUpdateParams.Item[] = [];

  for (const priceId of selectedPriceIds) {
    const existing = existingItems.find(
      (ei) => getSubscriptionItemPriceId(ei) === priceId
    );
    if (existing) {
      out.push({ id: existing.id });
    } else {
      out.push({ price: priceId });
    }
  }
  for (const existingItem of existingItems) {
    if (!selectedPriceIds.includes(getSubscriptionItemPriceId(existingItem))) {
      out.push({ id: existingItem.id, deleted: true });
    }
  }
  return out;
}

/**
 * Net change in subscription value when moving from existing items to
 * selectedPriceIds: positive when adding more than removing. Used for
 * proration_behavior (always_invoice when > 0, none otherwise).
 */
export function computeNetValueChange(
  existingItems: Stripe.SubscriptionItem[],
  selectedPriceIds: string[],
  stripePrices: Stripe.Price[]
): number {
  const getUnit = (priceId: string) =>
    stripePrices.find((p) => p.id === priceId)?.unit_amount ?? 0;
  const existingIds = new Set(existingItems.map(getSubscriptionItemPriceId));
  const selectedSet = new Set(selectedPriceIds);
  let add = 0;
  for (const pid of selectedPriceIds) {
    if (!existingIds.has(pid)) add += getUnit(pid);
  }
  let remove = 0;
  for (const item of existingItems) {
    const pid = getSubscriptionItemPriceId(item);
    if (!selectedSet.has(pid)) remove += getUnit(pid);
  }
  return add - remove;
}

/**
 * Sum of unit_amount for each subscription item. Use when items.data.price is
 * expanded (unit_amount only on the expanded Price object).
 */
export function computeSubscriptionCurrentTotal(
  existingItems: Stripe.SubscriptionItem[]
): number {
  return existingItems.reduce((sum, item) => {
    const p = item.price;
    const amt =
      typeof p === "object" && p && "unit_amount" in p ? p.unit_amount : null;
    return sum + (amt ?? 0);
  }, 0);
}

/**
 * Creates billing metadata for a Stripe subscription.
 * @param {Stripe.Response<Stripe.Price>} stripePrice - The Stripe price object
 * @param {string} stripeCustomerId - The Stripe customer ID
 * @param {string} strapiId - The Strapi user ID
 * @param {"gbp"|"eur"|"usd"} currency - The currency
 * @param {Coupon|null} coupon - The coupon object
 * @returns {object} The billing metadata object
 */
export function createBillingMeta(
  stripePrice: Stripe.Response<Stripe.Price>,
  stripeCustomerId: string,
  strapiId: string,
  currency: "gbp" | "eur" | "usd",
  coupon: CouponMeta | null
) {
  return {
    customer: stripeCustomerId,
    items: [
      {
        price: stripePrice.id,
      },
    ],
    currency: stripePrice.currency_options ? currency : stripePrice.currency,
    payment_behavior:
      "default_incomplete" as Stripe.SubscriptionCreateParams.PaymentBehavior,
    payment_settings: {
      save_default_payment_method:
        "on_subscription" as Stripe.SubscriptionCreateParams.PaymentSettings.SaveDefaultPaymentMethod,
    },
    expand: ["latest_invoice.payment_intent"],
    metadata: { strapiId },
    cancel_at_period_end: coupon?.couponType === "admin" ? true : false,
    discounts: coupon ? [{ promotion_code: coupon.promotionCodeId }] : [],
  };
}

/**
 * Retrieves all available memberships and their pricing meta from Stripe.
 * @returns {Promise<ProductPricingMeta>} The product pricing metadata
 */
export async function getStripeProducts() {
  // first get the active subject document ids in strapi
  const activeSubjectDocumentIds = (await getSubjects()).map(
    (s) => s.documentId
  );
  const stripeProducts = await stripe.products.list({
    active: true,
    limit: 100,
  });
  const stripePricesArrays = await Promise.all(
    stripeProducts.data.map(async (product) => {
      const list = await stripe.prices.list({
        product: product.id,
        active: true,
        limit: 100,
      });
      return list.data;
    })
  );
  const stripePrices = stripePricesArrays.flat();
  const productsWithPricing = stripeProducts.data.reduce(
    (acc: StripeProductMetadata, product) => {
      // get rid of old style
      if (product.metadata.accessLevel) {
        return acc;
      }
      // get the subject ids from the product metadata
      const includedSubjectIds = Object.entries(product.metadata)
        .filter(([key, value]) => {
          if (key.startsWith("subjectAccess")) return value;
        })
        .map(([key, value]) => value);
      // get the prices for the product
      const productPrices: CustomStripePrice[] = stripePrices.reduce(
        (priceAcc: CustomStripePrice[], price) => {
          if (price.product === product.id && price.unit_amount_decimal) {
            priceAcc.push({
              id: price.id,
              currency: price.currency,
              unitAmountDecimal: price.unit_amount_decimal,
              interval: price.recurring ? price.recurring.interval : null,
              intervalCount: price.recurring
                ? price.recurring.interval_count
                : null,
            });
          }
          return priceAcc;
        },
        []
      );

      if (isAllAccessProduct(product.name)) {
        // if the product is an all access product, add it to the all access product
        // filter out any non-active subject Ids
        acc.allAccessProduct = {
          id: product.id,
          name: product.name,
          includedSubjectIds: includedSubjectIds.filter((id) =>
            activeSubjectDocumentIds.includes(id)
          ),
          prices: productPrices,
        };
      }
      // otherwise, if some of the included subject ids are active
      // add it to the subject products
      else if (
        includedSubjectIds.some((id) => activeSubjectDocumentIds.includes(id))
      ) {
        acc.subjectProducts.push({
          id: product.id,
          name: product.name,
          includedSubjectIds: includedSubjectIds,
          prices: productPrices,
        });
      }
      return acc;
    },
    {
      allAccessProduct: {
        id: "",
        name: "",
        includedSubjectIds: [],
        prices: [],
      },
      subjectProducts: [],
    }
  );
  return productsWithPricing;
}

export function isAllAccessProduct(productName: string) {
  return productName.toLowerCase().includes("all access");
}

/**
 * Converts a Unix timestamp to an ISO date string.
 * @param {number} unixDate - The Unix timestamp
 * @returns {string} The ISO date string
 */
export function convertUnixTimestampToISOString(unixDate: number) {
  return new Date(unixDate * 1000).toISOString();
}

/**
 * Determines the coupon type from a Stripe coupon object.
 * @param {Stripe.Coupon|undefined} coupon - The Stripe coupon object
 * @returns {CouponType|null} The coupon type
 */
export function checkCouponCondition(couponId: string) {
  let type: CouponType = null;
  switch (couponId) {
    case "fMprSpIf":
    case "rv8Eo5B3":
    case "TgLeGkjL":
      type = "admin";
      break;
    case "lifetime":
      type = "lifetime";
      break;
  }
  return type;
}

/**
 * Counts the total number of seats (students) in a school membership.
 * @param {SchoolMembershipData["Students"]} studentMeta - The students array
 * @returns {number} The total number of seats
 */
export function countSeats(studentMeta: SchoolMembershipData["Students"]) {
  let count = 0;
  studentMeta.forEach((student) => (count += student.classNames.length));
  return count;
}

/**
 * Calculates billing totals, savings, and markdown price for a membership and coupon.
 * @param {SelectedSubscriptionMeta|undefined} membershipMeta - The membership meta data
 * @param {Coupon|null} coupon - The coupon object
 * @returns {{theoreticalMaxPrice: number, couponSavings: number, markdownPrice: number, totalSavings: number}} The billing totals
 */
export function getBillingTotals(
  selectedSubscriptionMeta: SelectedSubscriptionMeta | undefined,
  coupon: CouponMeta | null
) {
  const proxyAmount =
    selectedSubscriptionMeta?.items.reduce(
      (acc, item) => acc + item.unitAmount,
      0
    ) ?? 0;
  const theoreticalMaxPrice = proxyAmount * 1.6;
  const couponSavings = proxyAmount * ((coupon?.couponDiscount ?? 0) / 100);
  return {
    theoreticalMaxPrice,
    couponSavings,
    markdownPrice: proxyAmount - couponSavings,
    totalSavings: couponSavings + (theoreticalMaxPrice - proxyAmount),
  };
}

export function formatBillingPeriod(billingPeriod: string): string {
  const [interval, countStr] = billingPeriod.split("-");
  const count = parseInt(countStr);
  if (interval === "month") {
    return count === 1 ? "1 Month" : `${count} Months`;
  }
  if (interval === "year") {
    return count === 1 ? "1 Year" : `${count} Years`;
  }
  return billingPeriod;
}
